<template>
  <div class="locations">
    <div class="row">
      <div
        v-for="(location, idx) in locations"
        :key="idx"
        :class="{ 'col-sm-6 mb-4': !single, 'col-sm-12': single }"
      >
        <BCard class="location">
          <BCardText>
            <CertificationFormGroup :name="`locations.$each.${idx}.generalError`">
              <template slot-scope="{validator}">
                <InputHiddenControl id="'locations-general-' + idx"/>
              </template>
            </CertificationFormGroup>

            <CertificationFormGroup
              :name="`locations.$each.${idx}.name`"
              :can-moderate="false"
            >
              <template slot-scope="{ validator }">
                <LocationTypeAheadControl
                  :id="'location-name-' + idx"
                  v-model="location.name"
                  label="Nazwa obiektu"
                  :inline="single"
                  :disabled="disabled"
                  @input="touch(validator)"
                  @update:item="setLocationData(idx, $event)"
                />
              </template>
            </CertificationFormGroup>

            <CertificationFormGroup :name="`locations.$each.${idx}.city`">
              <template slot-scope="{ validator }">
                <InputControl
                  id="'location-city-' + idx"
                  v-model="location.city"
                  label="Miejscowość"
                  :inline="single"
                  :disabled="location.id > 0 || disabled"
                  @input="touch(validator)"
                />
              </template>
            </CertificationFormGroup>

            <div class="row">
              <div class="col-md-6">
                <CertificationFormGroup
                  v-if="!single"
                  :name="`locations.$each.${idx}.dateStart`"
                >
                  <template slot-scope="{ validator }">
                    <DateControl
                      :id="`location-date-start-` + idx"
                      v-model="locations[idx].dateStart"
                      label="Data rozpoczęcia wydarzenia"
                      :inline="single"
                      :disabled="disabled"
                      @input="touch(validator)"
                    />
                  </template>
                </CertificationFormGroup>
              </div>
              <div class="col-md-6">
                <CertificationFormGroup
                  v-if="!single"
                  :name="`locations.$each.${idx}.dateEnd`"
                >
                  <template slot-scope="{ validator }">
                    <DateControl
                      :id="`location-date-end-` + idx"
                      v-model="locations[idx].dateEnd"
                      label="Data zakończenia wydarzenia"
                      :allow-all-caps="true"
                      :disabled="disabled"
                      @input="touch(validator)"
                    />
                  </template>
                </CertificationFormGroup>
              </div>
            </div>
            <CertificationFormGroup :name="`locations.$each.${idx}.type`">
              <template slot-scope="{ validator }">
                <SelectControl
                  :id="'location-type-' + idx"
                  v-model="locations[idx].type"
                  :options="locationType"
                  :get-object="false"
                  label="Charakter obiektu"
                  :inline="single"
                  :disabled="disabled"
                  :close-on-select="false"
                  :max-height="500"
                  @input="touch(validator)"
                />
              </template>
            </CertificationFormGroup>

            <transition name="slide-fade">
              <CertificationFormGroup
                v-show="isHotel(idx)"
                :name="`locations.$each.${idx}.typeStars`"
              >
                <StarsControl
                  :id="'location-type-stars-' + idx"
                  v-model="locations[idx].typeStars"
                  :disabled="disabled"
                  label="Liczba gwiazdek"
                  :inline="single"
                />
              </CertificationFormGroup>
            </transition>

            <transition name="slide-fade">
              <CertificationFormGroup
                v-show="hasTypeOther(idx)"
                :name="`locations.$each.${idx}.typeOther`"
              >
                <template slot-scope="{ validator }">
                  <InputControl
                    :id="'location-type-other-' + idx"
                    v-model="locations[idx].typeOther"
                    :disabled="disabled"
                    label="Jakie inne?"
                    :inline="single"
                    @input="touch(validator)"
                  />
                </template>
              </CertificationFormGroup>
            </transition>

            <CertificationFormGroup :name="`locations.$each.${idx}.features`">
              <template slot-scope="{ validator }">
                <SelectControl
                  :id="'location-type-' + idx"
                  v-model="locations[idx].features"
                  :disabled="disabled"
                  :options="locationFeatureType"
                  :get-object="false"
                  :multiple="true"
                  :close-on-select="false"
                  label="Obiekt, w którym odbywa się wydarzenie posiada"
                  description="Proszę wybrać wszystkie dostępne atrakcje"
                  :inline="single"
                  :max-height="500"
                  :block-tag="true"
                  @input="touch(validator)"
                />
              </template>
            </CertificationFormGroup>

            <transition name="slide-fade">
              <CertificationFormGroup
                v-show="hasFeaturesOther(idx)"
                :name="`locations.$each.${idx}.featuresOther`"
              >
                <template slot-scope="{ validator }">
                  <InputControl
                    :id="'location-features-other-' + idx"
                    v-model="locations[idx].featuresOther"
                    label="Jakie inne?"
                    :inline="single"
                    :disabled="disabled"
                    @input="touch(validator)"
                  />
                </template>
              </CertificationFormGroup>
            </transition>

            <CertificationFormGroup :name="`locations.$each.${idx}.isCentral`">
              <TrueFalseControl
                :id="'location-is-central-' + idx"
                v-model="locations[idx].isCentral"
                :disabled="disabled"
                label="Obiekt znajduje się w centralnej lokalizacji w stosunku do większości uczestników wydarzenia"
                :inline="single"
              />
            </CertificationFormGroup>

            <CertificationFormGroup :name="`locations.$each.${idx}.easyAccess`">
              <TrueFalseControl
                :id="'location-easy-access-' + idx"
                v-model="locations[idx].easyAccess"
                :disabled="disabled"
                label="Obiekt jest łatwo dostępny dla uczestników"
                label-description="dostępne są środki komunikacji publicznej, obiekt znajduje się blisko lotniska, stacji kolejowej itp."
                :inline="single"
              />
            </CertificationFormGroup>
          </BCardText>
          <Btn
            v-if="!single && !disabled"
            variant="outline-danger"
            size="sm"
            icon="map-marker-minus"
            @click="removeLocation(idx)"
          >
            Usuń
          </Btn>
        </BCard>
      </div>
      <div v-if="!single && !disabled" class="col-sm-6">
        <Btn
          variant="outline-primary"
          icon="map-marker-plus"
          @click="addLocation"
        >
          Dodaj lokalizację
        </Btn>
      </div>
    </div>
  </div>
</template>

<script>
import LocationTypeAheadControl from "@/components/Controls/LocationTypeAheadControl";
import BaseFormControl from "@/components/Base/Form/BaseFormControl";
import InputControl from "@/components/Controls/InputControl";
import DateControl from "@/components/Controls/DateControl";
import TrueFalseControl from "@/components/Controls/TrueFalseControl";
import SelectControl from "@/components/Controls/SelectControl";
import StarsControl from "@/components/Controls/StarsControl";
import InputHiddenControl from '@/components/Controls/InputHiddenControl';
import { locationType, locationFeatureType } from "./data";
import { LocationModel, EventDateModel } from "@/model/";

export default {
  name: "CertificationFormLocations",
  components: {
    InputControl,
    DateControl,
    TrueFalseControl,
    SelectControl,
    StarsControl,
    LocationTypeAheadControl,
    InputHiddenControl
  },
  extends: BaseFormControl,
  props: {
    value: {
      type: Array,
      required: true
    },
    single: {
      type: Boolean,
      default: false
    },
    formDate: {
      type: Object,
      default: () => new EventDateModel()
    }
  },
  data() {
    return {
      locations: this.value,
      locationType,
      locationFeatureType,
      lodationsUrl: `${process.env.VUE_APP_XHR_ROOT}certification/location/?query=%query`
    };
  },
  created() {
    this.prepareLocations();
  },
  computed: {},
  watch: {
    value(newValue) {
      this.locations = newValue;
    },
    single() {
      this.prepareLocations();
    },
    formDate: {
      deep: true,
      handler() {
        this.prepareLocations();
      }
    }
  },
  methods: {
    setLocationData(idx, data) {
      if (data && data.id) {
        this.$set(this.locations[idx], "id", data.id);
        this.$set(this.locations[idx], "city", data.city);
        // this.$set(
        //   this.locations[idx],
        //   "features",
        //   ("" + data.features).split(",")
        // );
        // this.$set(
        //   this.locations[idx],
        //   "featuresOther",
        //   "" + data.featuresOther
        // );
        // this.$set(this.locations[idx], "type", data.type);
        // this.$set(this.locations[idx], "typeOther", "" + data.typeOther);
        // this.$set(
        //   this.locations[idx],
        //   "typeStars",
        //   parseInt(0 + data.typeStars)
        // );
      } else if (!data.id) {
        this.$set(this.locations[idx], "id", -1);
      }
    },
    hasTypeOther(idx) {
      return this.locations[idx].type == "other";
    },
    hasFeaturesOther(idx) {
      return (
        this.locations[idx].features.filter(item => item == "other").length > 0
      );
    },
    isHotel(idx) {
      let type = this.locations[idx].type;
      return type === "hotel_1" || type === "hotel_2";
    },
    addLocation() {
      let location = new LocationModel();
      this.setDefaultDate(location);
      this.locations.push(location);
    },
    removeLocation(idx) {
      this.locations.splice(idx, 1);
    },
    prepareLocations() {
      if (this.single === true) {
        this.locations.splice(1);
      }
      for (let i = 0; i < this.locations.length; i++) {
        this.setDefaultDate(this.locations[i]);
      }
    },
    setDefaultDate(location) {
      if (!location.dateStart || this.single === true) {
        location.dateStart = this.formDate.dateStart;
        location.dateEnd = this.formDate.dateEnd;
      }
    }
  }
};
</script>

<style scoped></style>
